import React from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { EXAM_TYPES } from '../../../utils/studyPlanMockData';

interface ExamTypeStepProps {
  onNext: (data: { examType: string }) => void;
  defaultValue?: string;
}

interface FormData {
  examType: string;
}

const ExamTypeStep: React.FC<ExamTypeStepProps> = ({ onNext, defaultValue }) => {
  const { register, handleSubmit, formState: { errors }, watch } = useForm<FormData>({
    defaultValues: { examType: defaultValue || '' }
  });

  const selectedExam = watch('examType');

  const onSubmit = (data: FormData) => {
    onNext(data);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="step-content"
    >
      <div className="container-fluid py-4">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="text-center mb-5">
              <h2 className="display-6 fw-bold text-primary mb-3">
                Select Your Exam Type
              </h2>
              <p className="lead text-muted">
                Choose the competitive exam you're preparing for to get a personalized study plan
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="row g-4 mb-5">
                {EXAM_TYPES.map((exam) => (
                  <div key={exam.id} className="col-md-4">
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`card h-100 border-2 cursor-pointer position-relative ${
                        selectedExam === exam.id 
                          ? 'border-primary bg-light shadow-sm' 
                          : 'border-light hover-shadow'
                      }`}
                      onClick={() => {
                        const input = document.getElementById(`exam-${exam.id}`) as HTMLInputElement;
                        if (input) input.click();
                      }}
                    >
                      {selectedExam === exam.id && (
                        <div className="position-absolute top-0 end-0 m-2">
                          <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" 
                               style={{ width: '24px', height: '24px' }}>
                            <i className="fas fa-check" style={{ fontSize: '12px' }}></i>
                          </div>
                        </div>
                      )}
                      <div className="card-body text-center p-4">
                        <div className="mb-3">
                          <div className={`rounded-circle mx-auto d-flex align-items-center justify-content-center ${
                            selectedExam === exam.id ? 'bg-primary text-white' : 'bg-light text-primary'
                          }`} style={{ width: '60px', height: '60px' }}>
                            <i className="fas fa-graduation-cap fa-lg"></i>
                          </div>
                        </div>
                        
                        <h5 className={`card-title mb-2 fw-bold ${
                          selectedExam === exam.id ? 'text-primary' : 'text-dark'
                        }`}>
                          {exam.name}
                        </h5>
                        
                        <p className={`card-text small mb-3 ${
                          selectedExam === exam.id ? 'text-dark' : 'text-muted'
                        }`}>
                          {exam.description}
                        </p>
                        
                        <div className="form-check">
                          <input
                            {...register('examType', { required: 'Please select an exam type' })}
                            className="form-check-input"
                            type="radio"
                            value={exam.id}
                            id={`exam-${exam.id}`}
                            style={{ pointerEvents: 'none' }}
                          />
                          <label className={`form-check-label fw-semibold ${
                            selectedExam === exam.id ? 'text-primary' : 'text-dark'
                          }`} htmlFor={`exam-${exam.id}`}>
                            {selectedExam === exam.id ? (
                              <>
                                <i className="fas fa-check-circle me-1"></i>
                                Selected
                              </>
                            ) : (
                              `Select ${exam.name}`
                            )}
                          </label>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                ))}
              </div>

              {errors.examType && (
                <div className="alert alert-danger" role="alert">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {errors.examType.message}
                </div>
              )}

              <div className="text-center">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg px-5"
                  disabled={!selectedExam}
                >
                  Continue to Next Step
                  <i className="fas fa-arrow-right ms-2"></i>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ExamTypeStep;